import { cleanBullets, contactLine, dates, templateById } from "@/lib/resume";

const rgb = (a, o = 1) => `rgba(${a.join(",")},${o})`;
const FONTS = { professional: "Georgia, 'Times New Roman', serif" };

const Section = ({ t, title, children }) => {
  const c = rgb(t.accent);
  const head = {
    modern: <h3 style={{ color: c, borderBottom: `1px solid ${c}`, paddingBottom: 4 }} className="text-[13px] font-bold uppercase tracking-wider">{title}</h3>,
    professional: <h3 style={{ borderBottom: "3px double #222", paddingBottom: 3 }} className="text-[13px] font-bold uppercase tracking-wider">{title}</h3>,
    minimal: <h3 className="text-[12px] font-semibold uppercase tracking-[0.25em] text-neutral-500">{title}</h3>,
    creative: <h3 style={{ color: c }} className="text-[13px] font-extrabold uppercase tracking-wide">{title}<span className="mt-1 block h-[3px] w-8" style={{ background: c }} /></h3>,
    healthcare: <h3 style={{ color: c, borderLeft: `4px solid ${c}`, paddingLeft: 8 }} className="text-[13px] font-bold uppercase tracking-wide">{title}</h3>,
  }[t.id];
  return <section className="mt-5">{head}<div className="mt-2.5 space-y-3">{children}</div></section>;
};

const Row = ({ left, right, sub }) => (
  <div>
    <div className="flex items-baseline justify-between gap-4"><span className="font-semibold">{left}</span>{right && <span className="shrink-0 text-[11px] text-neutral-500">{right}</span>}</div>
    {sub && <div className="text-[12px] italic text-neutral-600">{sub}</div>}
  </div>
);

const Header = ({ t, p }) => {
  const c = rgb(t.accent), contact = contactLine(p);
  if (t.id === "creative") return (
    <header className="-mx-12 -mt-12 mb-2 px-12 pb-6 pt-10 text-white" style={{ background: c }}>
      <h1 className="text-[30px] font-extrabold leading-tight">{p.full_name || "Your Name"}</h1>
      <p className="text-[15px] opacity-90">{p.title}</p>
      <p className="mt-2 text-[11px] opacity-90">{contact.join("  |  ")}</p>
    </header>
  );
  const center = t.id === "professional";
  return (
    <header className={center ? "text-center" : ""}>
      <h1 className="text-[28px] font-bold leading-tight text-neutral-900">{p.full_name || "Your Name"}</h1>
      {p.title && <p className="text-[15px]" style={{ color: ["modern", "healthcare"].includes(t.id) ? c : "#555" }}>{p.title}</p>}
      <p className="mt-1.5 text-[11px] text-neutral-500">{contact.join("  |  ")}</p>
      {t.id === "modern" && <div className="mt-3 h-[3px] w-12" style={{ background: c }} />}
    </header>
  );
};

export const ResumeDoc = ({ data: r, template }) => {
  const t = templateById(template);
  const certs = r.certifications.some((c) => c.name) && (
    <Section t={t} title={t.id === "healthcare" ? "Licenses & Certifications" : "Certifications"}>
      {r.certifications.filter((c) => c.name).map((c) => <Row key={c.id} left={[c.name, c.issuer].filter(Boolean).join(" — ")} right={c.date} />)}
    </Section>
  );
  return (
    <article className="min-h-[1056px] w-[816px] bg-white p-12 text-[13px] leading-relaxed text-neutral-800" style={{ fontFamily: FONTS[t.id] || "Helvetica, Arial, sans-serif" }} data-testid={`resume-doc-${t.id}`}>
      <Header t={t} p={r.personal} />
      {r.summary && <Section t={t} title="Summary"><p>{r.summary}</p></Section>}
      {t.id === "healthcare" && certs}
      {r.experience.length > 0 && (
        <Section t={t} title="Experience">
          {r.experience.map((e) => (
            <div key={e.id}>
              <Row left={e.role || "Role"} right={dates(e)} sub={[e.company, e.location].filter(Boolean).join(" · ")} />
              <ul className="mt-1 list-disc space-y-0.5 pl-5">{cleanBullets(e.bullets).map((b, i) => <li key={i}>{b}</li>)}</ul>
            </div>
          ))}
        </Section>
      )}
      {r.education.length > 0 && (
        <Section t={t} title="Education">
          {r.education.map((e) => <div key={e.id}><Row left={e.degree || "Degree"} right={dates(e)} sub={[e.school, e.location].filter(Boolean).join(" · ")} />{e.details && <p className="text-[12px]">{e.details}</p>}</div>)}
        </Section>
      )}
      {r.skills.length > 0 && <Section t={t} title="Skills"><p>{r.skills.join(" · ")}</p></Section>}
      {t.id !== "healthcare" && certs}
    </article>
  );
};
